
import React, { useState, useRef, useEffect } from 'react';
import { Send, AlertCircle, Sparkles, RefreshCw, Lock, Zap, Crown, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Sender, Message } from '../types/tutor';
import { sendMessageToGeminiStream, parseResponse } from '../services/geminiService';
import { INITIAL_GREETING, OSP_STARTER_CHIPS } from '../constants/tutor';
import ChatMessageBubble from './tutor/ChatMessageBubble';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../AuthContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { FEATURE_FLAGS, isBetaTester } from '../config/featureFlags';
import { useStreak } from '../contexts/StreakContext';
import {

    getDailyUsage,
    incrementDailyUsage,
    saveMessage,
    createChat,
    getActiveChat,
    getChatById,
    getOspEntitlementStatus,
    OspEntitlementStatus,
    OspEntitlementState,
    OSP_FREE_LIMIT,
    OSP_PRO_LIMIT
} from '../services/ospFirebaseService';
import { getUserPreferences } from '../services/preferencesService';

interface TutorModuleProps {
    requestedChatId?: string | null;
    onChatChanged?: (chatId: string) => void;
    forceNewChat?: boolean;
    onForceNewChatHandled?: () => void;
    autoprompt?: string | null;
    onAutopromptHandled?: () => void;
    onBackToDashboard?: () => void;
}

const TutorModule: React.FC<TutorModuleProps> = ({ requestedChatId, onChatChanged, forceNewChat, onForceNewChatHandled, autoprompt, onAutopromptHandled, onBackToDashboard }) => {
    const { currentUser } = useAuth();
    const { trackActivity } = useStreak();
    const navigate = useNavigate();
    const [messages, setMessages] = useState<Message[]>([]);
    const messagesRef = useRef<Message[]>([]);
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentChatId, setCurrentChatId] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const isProcessing = useRef(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Usage & Subscription State
    const [isPro, setIsPro] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [hasUsedTrial, setHasUsedTrial] = useState(false);
    const [dailyUsage, setDailyUsage] = useState(0);
    const [dailyLimit, setDailyLimit] = useState(OSP_FREE_LIMIT);
    const [limitReached, setLimitReached] = useState(true); // Block until usage is confirmed
    // Refs to always hold latest values — avoids stale closures in async callbacks (e.g. autoprompt → handleSendMessage)
    const dailyLimitRef = useRef(dailyLimit);
    const isProRef = useRef(isPro);
    const [isBlocked, setIsBlocked] = useState(false); // Valid state for abuse block
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [subscriptionType, setSubscriptionType] = useState<'trial' | 'full' | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [entitlementState, setEntitlementState] = useState<OspEntitlementState | null>(null);
    const [isLoadingEntitlement, setIsLoadingEntitlement] = useState(true);
    const [emojiLevel, setEmojiLevel] = useState<'none' | 'medium' | 'many'>('medium');

    // Stream Buffer State
    const streamBufferRef = useRef<{ id: string, chatId: string, fullText: string, displayedText: string, isDone: boolean } | null>(null);
    const truncationRetriedRef = useRef<string | null>(null); // tracks last retried message to prevent infinite loops

    // Initial Data Load
    useEffect(() => {
        let unsubscribeUser: () => void;

        const init = async () => {
            if (!currentUser) {
                setInitialMessage();
                setIsLoadingEntitlement(false);
                return;
            }

            // 1. Real-time Subscription & Entitlement Check
            const userRef = doc(db, 'users', currentUser.uid);
            unsubscribeUser = onSnapshot(userRef, (userSnap) => {
                const userData = userSnap.exists() ? userSnap.data() : null;

                // Entitlement State
                const entitlement = getOspEntitlementStatus(userData);
                setEntitlementState(entitlement);

                // Derived Subscription Status (simulating checkSubscriptionStatus logic but real-time)
                const isBeta = currentUser?.email ? isBetaTester(currentUser.email) : false;

                // DEBUG: Log beta status
                console.log("[DEBUG] User:", currentUser?.email, "Is Beta:", isBeta);

                const _isPro = entitlement.status === OspEntitlementStatus.PRO_ACTIVE
                    || isBeta;
                const _hasUsedTrial = userData?.ospUsage?.hasUsedTrial === true;
                const _isBlocked = userData?.trialAbuseBlocked === true;
                const _subType = userData?.ospSubscriptionType || null;

                setIsPro(_isPro);
                setHasUsedTrial(_hasUsedTrial);
                setIsBlocked(_isBlocked);
                setSubscriptionType(_subType);

                const newLimit = _isPro ? (isBeta ? 9999 : OSP_PRO_LIMIT) : OSP_FREE_LIMIT;
                console.log("[DEBUG] New Limit:", newLimit);
                setDailyLimit(newLimit);

                setIsLoadingEntitlement(false);
            }, (error) => {
                console.error("Error fetching user data:", error);
                setIsLoadingEntitlement(false);
            });

            // 2. Load Usage (One-time fetch is okay for usage, or could be real-time too)
            const usage = await getDailyUsage(currentUser.uid);
            setDailyUsage(usage);
            // Immediately apply limit check to prevent bypass exploit on remount
            // Use dailyLimitRef which may already be updated by onSnapshot (from cache)
            setLimitReached(usage >= dailyLimitRef.current);

            // 3. Load Chat - Skip if we're forcing a new chat or auto-prompting 
            // to avoid overwriting the newly created chat with stale remote data
            if (!forceNewChat && !autoprompt) {
                const activeChat = await getActiveChat(currentUser.uid);
                if (activeChat) {
                    setMessages(activeChat.messages);
                    setCurrentChatId(activeChat.chatId);
                    isPinnedToBottom.current = true;
                    onChatChanged?.(activeChat.chatId);
                } else {
                    setInitialMessage();
                    isPinnedToBottom.current = true;
                }
            } else {
                // If there's an autoprompt or forceNewChat, it will be handled by their respective useEffects.
                // Setting initial message here prevents screen flashing before the new chat is created.
                setInitialMessage();
                isPinnedToBottom.current = true;
            }

            // 4. Load Emoji Preferences
            try {
                const prefs = await getUserPreferences(currentUser.uid);
                setEmojiLevel(prefs.emojiLevel);
            } catch (error) {
                console.error("Error loading user preferences:", error);
                // Keep default 'medium' if error
            }
        };

        init();

        return () => {
            if (unsubscribeUser) unsubscribeUser();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.uid]); // Bind specifically to uid to prevent auth refresh from re-running init

    // Cleanup stream on component unmount
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    // Handle chat switching from sidebar
    useEffect(() => {
        if (!requestedChatId || !currentUser || requestedChatId === currentChatId) return;
        const loadChat = async () => {
            const chat = await getChatById(currentUser.uid, requestedChatId);
            if (chat) {
                setMessages(chat.messages);
                setCurrentChatId(chat.chatId);
                isPinnedToBottom.current = true;
            }
        };
        loadChat();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedChatId, currentUser?.uid]);

    // Handle "new chat" request from sidebar (skip if autoprompt will handle it)
    useEffect(() => {
        if (!forceNewChat || !currentUser || autoprompt) return;
        let cancelled = false;
        const startNewChat = async () => {
            const newId = await createChat(currentUser.uid);
            if (cancelled) return;
            setCurrentChatId(newId);
            setInitialMessage();
            isPinnedToBottom.current = true;
            onChatChanged?.(newId);
            onForceNewChatHandled?.();
        };
        startNewChat();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceNewChat, currentUser?.uid]);

    // Refs to track processed prompts to prevent duplicate chat creations
    const processedUrlPromptRef = useRef<string | null>(null);
    const processedAutopromptRef = useRef<string | null>(null);

    // Check for auto-prompt from URL (e.g. from OSP Mission)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const prompt = params.get('prompt');

        if (!prompt || isProcessing.current || !currentUser || processedUrlPromptRef.current === prompt) return;

        let cancelled = false;
        const run = async () => {
            try {
                const newChatId = await createChat(currentUser.uid);
                if (cancelled) return;
                setCurrentChatId(newChatId);
                setInitialMessage();
                onChatChanged?.(newChatId);
                window.history.replaceState({}, '', window.location.pathname);
                // handleSendMessage with explicit chatId — no nested setTimeout needed
                const sent = await handleSendMessage(prompt, newChatId);
                if (sent) {
                    processedUrlPromptRef.current = prompt;
                }
            } catch (e) {
                console.error("Failed to process URL prompt", e);
            }
        };
        run();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.uid]); // Run when user auth is ready

    // Reset processedAutopromptRef when autoprompt is cleared
    useEffect(() => {
        if (!autoprompt) {
            processedAutopromptRef.current = null;
        }
    }, [autoprompt]);

    // Handle external autoprompt (e.g., from sidebar activities)
    useEffect(() => {
        if (!autoprompt || isProcessing.current || !currentUser || processedAutopromptRef.current === autoprompt) return;

        let cancelled = false;
        const run = async () => {
            try {
                const newChatId = await createChat(currentUser.uid);
                if (cancelled) return;
                setCurrentChatId(newChatId);
                setInitialMessage();
                onChatChanged?.(newChatId);
                onForceNewChatHandled?.(); // Clear forceNewChat if it was set
                // Send message with explicit chatId — no nested setTimeout needed
                const sent = await handleSendMessage(autoprompt, newChatId);
                if (sent) {
                    processedAutopromptRef.current = autoprompt;
                    if (!cancelled) onAutopromptHandled?.();
                }
            } catch (e) {
                console.error("Failed to process external autoprompt", e);
            }
        };
        run();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoprompt, currentUser?.uid]);

    // Check limit whenever usage changes
    useEffect(() => {
        setLimitReached(dailyUsage >= dailyLimit);
    }, [dailyUsage, dailyLimit]);

    // Keep refs in sync with state for use in async callbacks
    useEffect(() => { dailyLimitRef.current = dailyLimit; }, [dailyLimit]);
    useEffect(() => { isProRef.current = isPro; }, [isPro]);

    const setInitialMessage = () => {
        setMessages([{
            id: 'init-1',
            text: INITIAL_GREETING,
            sender: Sender.BOT,
            timestamp: new Date(),
        }]);
    };

    // Smooth Typing Effect
    useEffect(() => {
        if (!streamBufferRef.current) return;

        const interval = setInterval(() => {
            const buffer = streamBufferRef.current;
            if (!buffer) return;

            // If we have more text to display
            if (buffer.displayedText.length < buffer.fullText.length) {
                // Reveal speed
                const lag = buffer.fullText.length - buffer.displayedText.length;
                const step = Math.max(1, Math.min(5, Math.ceil(lag / 10)));

                const nextChunk = buffer.fullText.slice(buffer.displayedText.length, buffer.displayedText.length + step);
                buffer.displayedText += nextChunk;

                setMessages(prev => prev.map(msg =>
                    msg.id === buffer.id
                        ? { ...msg, text: buffer.displayedText }
                        : msg
                ));
            } else if (buffer.isDone) {
                const { chatId: bufferChatId } = buffer;
                streamBufferRef.current = null;
                setIsLoading(false);
                isProcessing.current = false;

                // Save the FINAL complete bot message to Firebase using chatId from buffer (not closure)
                if (currentUser && bufferChatId) {
                    const botMsgToSave: Message = {
                        id: buffer.id,
                        text: buffer.fullText,
                        sender: Sender.BOT,
                        timestamp: new Date()
                    };
                    saveMessage(currentUser.uid, bufferChatId, botMsgToSave).catch(console.error);
                }

                setTimeout(() => inputRef.current?.focus(), 100);
            }
        }, 30);

        return () => clearInterval(interval);
    }, [isLoading, currentUser]); // chatId now comes from buffer, not closure

    // Track if the user is "pinned" to the bottom
    const isPinnedToBottom = useRef(true);

    const handleScroll = () => {
        const chatContainer = chatContainerRef.current;
        if (!chatContainer) return;

        const { scrollTop, scrollHeight, clientHeight } = chatContainer;
        // If user is within 100px of the bottom, they are "pinned"
        // We use a slightly smaller threshold to detect intentional scroll up
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;

        // Only update if the value changes to avoid thrashing (though refs don't trigger re-render)
        isPinnedToBottom.current = isAtBottom;
    };

    // Auto-scroll effect
    useEffect(() => {
        // Only auto-scroll if we are ALREADY pinned. 
        // If the user scrolled up (isPinnedToBottom is false), we do NOT scroll.
        if (isPinnedToBottom.current) {
            scrollToBottom(false); // Pass false to indicate this is an auto-scroll, not a forced action
        }
    }, [messages, isLoading]); // Trigger on any message change

    const scrollToBottom = (forcePin = true, behavior: ScrollBehavior = 'smooth') => {
        const chatContainer = chatContainerRef.current;
        if (chatContainer) {
            chatContainer.scrollTo({
                top: chatContainer.scrollHeight,
                behavior
            });

            // Only force the pin state if this was an explicit action (like sending a message),
            // not just a passive auto-scroll update.
            if (forcePin) {
                isPinnedToBottom.current = true;
            }
        }
    };

    // Auto-resize textarea
    useEffect(() => {
        const textarea = inputRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
        }
    }, [inputText]);

    const handleSendMessage = async (overrideText?: string, overrideChatId?: string): Promise<boolean> => {
        const textToSend = overrideText || inputText;
        if (!textToSend.trim() || isLoading || isProcessing.current || !currentUser) return false;

        // CHECK LIMITS AGAIN (use refs to avoid stale closure values)
        const currentUsage = await getDailyUsage(currentUser.uid); // Fetch fresh
        if (currentUsage >= dailyLimitRef.current) {
            setDailyUsage(currentUsage);
            setLimitReached(true);
            if (!isProRef.current) navigate('/upgrade');
            return false;
        }

        isProcessing.current = true;
        const currentInput = textToSend.trim();

        // Ensure Chat ID exists
        let chatId = overrideChatId || currentChatId;
        if (!chatId) {
            chatId = await createChat(currentUser.uid);
            setCurrentChatId(chatId);
        }

        const userMsg: Message = {
            id: Date.now().toString(),
            text: currentInput,
            sender: Sender.USER,
            timestamp: new Date(),
        };

        setInputText('');
        setIsLoading(true);
        isPinnedToBottom.current = true; // Force scroll to bottom on new message

        // Optimistic Save User Msg
        saveMessage(currentUser.uid, chatId, userMsg).catch(console.error);

        // Track streak activity
        trackActivity('ai_tutor_used', 1);

        const botMsgId = (Date.now() + 1).toString();
        const placeholderMsg: Message = {
            id: botMsgId,
            text: '',
            internalAnalysis: '',
            sender: Sender.BOT,
            timestamp: new Date(),
        };

        // Single atomic state update to safely add both messages to the most current previous state
        setMessages(prev => [...prev, userMsg, placeholderMsg]);

        // Initialize buffer with explicit chatId to avoid stale closure in typing effect
        streamBufferRef.current = { id: botMsgId, chatId, fullText: "", displayedText: "", isDone: false };

        // Create abort controller for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        let accumulatedRawText = "";

        try {
            const token = await currentUser.getIdToken();

            // Use messagesRef to get synchronous access to the chat history prior to this turn,
            // then explicitly append the new userMsg to ensure the AI gets the complete context.
            const historyForApi = [...messagesRef.current, userMsg];

            const stream = sendMessageToGeminiStream(
                historyForApi, currentInput, emojiLevel, undefined, abortController.signal, token
            );

            for await (const chunk of stream) {
                if (chunk) {
                    accumulatedRawText += chunk;
                    const parsed = parseResponse(accumulatedRawText);
                    if (streamBufferRef.current) {
                        streamBufferRef.current.fullText = parsed.tutorResponse;
                    }
                }
            }

            // Only increment usage after a meaningful response was received
            if (accumulatedRawText.length > 10) {
                incrementDailyUsage(currentUser.uid).catch(console.error);
                setDailyUsage(prev => prev + 1);
            }

            // Detect truncated responses (Netlify timeout — stream ended without sentinel)
            const finalParsed = parseResponse(accumulatedRawText);
            if (accumulatedRawText.length > 50 && !finalParsed.isComplete) {
                const alreadyRetried = truncationRetriedRef.current === currentInput;
                if (!alreadyRetried) {
                    console.warn("[OSP Tutor] Stream truncated (no sentinel). Auto-retrying once.");
                    truncationRetriedRef.current = currentInput;
                    // Remove the truncated bot message and the duplicate user message, then retry
                    setMessages(prev => prev.filter(m => m.id !== botMsgId));
                    setTimeout(() => handleSendMessage(currentInput, overrideChatId || currentChatId || undefined), 300);
                } else {
                    // Already retried — give up and show a message
                    console.warn("[OSP Tutor] Retry also truncated. Showing error.");
                    truncationRetriedRef.current = null;
                    const warningMsg: Message = {
                        id: (Date.now() + 2).toString(),
                        text: "*Odpověď byla opakovaně zkrácena. Zkuste odeslat zprávu znovu za chvíli.*",
                        sender: Sender.SYSTEM,
                        timestamp: new Date(),
                    };
                    setMessages(prev => [...prev, warningMsg]);
                }
            }

            return true;
        } catch (error) {
            console.error(error);
            // Remove the empty bot placeholder before adding error message
            setMessages(prev => prev.filter(m => m.id !== botMsgId));
            streamBufferRef.current = null;

            const errorMsg: Message = {
                id: (Date.now() + 2).toString(),
                text: abortController.signal.aborted
                    ? "Odpověď trvala příliš dlouho a byla přerušena. Zkuste to prosím znovu."
                    : "Omlouvám se, spojení bylo přerušeno. Zkuste to prosím znovu.",
                sender: Sender.SYSTEM,
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMsg]);
            setIsLoading(false);
            isProcessing.current = false;
            return false;
        } finally {
            abortControllerRef.current = null;
            if (streamBufferRef.current) {
                streamBufferRef.current.isDone = true;
            } else {
                setIsLoading(false);
                isProcessing.current = false;
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleReset = async () => {
        if (!currentUser) return;
        if (window.confirm("Opravdu chcete začít nový chat?")) {
            const newId = await createChat(currentUser.uid);
            setCurrentChatId(newId);
            setInitialMessage();
            onChatChanged?.(newId);
        }
    };



    return (
        <div className="flex flex-col h-full w-full bg-light-bg dark:bg-dark-bg relative overflow-hidden">
            {/* Maintenance Mode Overlay */}
            {FEATURE_FLAGS.OSP_TUTOR_MAINTENANCE && (
                <div className="absolute inset-0 z-[70] bg-background/95 backdrop-blur-md flex items-center justify-center p-4">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-card w-full max-w-lg rounded-2xl border border-border shadow-2xl overflow-hidden p-8 text-center"
                    >
                        <div className="w-20 h-20 bg-amber-500/10 rounded-full mx-auto flex items-center justify-center mb-6">
                            <AlertCircle className="w-10 h-10 text-amber-500" />
                        </div>
                        <h3 className="text-2xl font-bold mb-3 text-foreground">OSP Tutor je dočasně nedostupný 🚧</h3>
                        <p className="text-muted-foreground mb-6">
                            Právě provádíme důležité vylepšení. AI tutor bude brzy zpět s ještě lepší podporou pro vaše studium!
                        </p>
                        <div className="bg-muted/30 p-4 rounded-lg mb-6">
                            <p className="text-sm text-muted-foreground">
                                💡 <strong>Tip:</strong> Mezitím můžete pokračovat v procvičování otázek v hlavním modulu.
                            </p>
                        </div>
                        <button
                            onClick={() => window.location.href = '/'}
                            className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-bold hover:opacity-90 transition-opacity"
                        >
                            Zpět na přehled
                        </button>
                    </motion.div>
                </div>
            )}

            {/* Mobile back button */}
            {onBackToDashboard && (
                <div className="md:hidden px-3 py-2 border-b border-border bg-card/50 backdrop-blur-sm">
                    <button
                        onClick={onBackToDashboard}
                        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        <span className="font-medium">Zpět</span>
                    </button>
                </div>
            )}

            {/* Header - Hidden on mobile, visible on desktop */}
            <div className="hidden md:block px-5 py-2 border-b border-border bg-card/50 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                    {/* Back button */}
                    {onBackToDashboard && (
                        <button
                            onClick={onBackToDashboard}
                            className="flex-shrink-0 mt-2 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            title="Zpět na přehled"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                    )}
                    {/* Icon */}
                    <div className="relative flex-shrink-0 mt-1">
                        <Sparkles className="w-6 h-6 text-amber-400 fill-amber-400" />
                        <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-background"></div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 flex flex-col justify-center min-h-[3.5rem]">
                        <div className="flex items-center gap-2 mb-0.5">
                            <h2 className="font-bold text-lg text-foreground leading-none">OSP Tutor</h2>
                            <button
                                onClick={handleReset}
                                className="text-muted-foreground hover:text-foreground transition-colors p-0.5 hover:bg-muted rounded-full"
                                title="Nový chat"
                            >
                                <RefreshCw size={12} />
                            </button>
                        </div>

                        <div className="hidden sm:block text-xs text-muted-foreground mb-1.5">
                            Tvůj osobní průvodce studiem
                        </div>

                        <div className="flex items-center gap-2 flex-wrap min-h-[0px]">
                            {/* Status Badge */}
                            <div className={cn(
                                "text-[10px] px-0 py-0.5 font-bold uppercase tracking-wider",
                                isPro ? "text-green-500" : "text-muted-foreground"
                            )}>
                                {isPro ? "PREMIUM AKTIVNÍ" : "VERZE ZDARMA"}
                            </div>

                            {/* Action Button - Based on Entitlement Status */}
                            {isLoadingEntitlement ? (
                                <div className="h-6 w-24 bg-muted animate-pulse rounded-full"></div>
                            ) : (
                                <>
                                    {!isPro && (
                                        <button
                                            onClick={() => navigate('/upgrade')}
                                            className="flex items-center gap-1.5 px-3 py-1 bg-yellow-400 hover:bg-yellow-300 border-b-[2px] border-yellow-600 active:border-b-0 active:translate-y-[2px] text-yellow-950 font-black text-[10px] tracking-wide rounded-lg transition-all"
                                        >
                                            <Crown size={10} />
                                            <span className="whitespace-nowrap">ZKUS ZDARMA</span>
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Chat Area */}
            <div
                ref={chatContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent"
            >
                <div className="max-w-3xl mx-auto flex flex-col min-h-full justify-end">
                    {messages.length <= 1 && (
                        <div className="flex flex-col items-center justify-center p-8 mb-8 text-center opacity-60">
                            <div className="w-24 h-24 bg-gradient-to-br from-primary/20 to-purple-500/20 rounded-full flex items-center justify-center mb-6">
                                <Sparkles className="w-12 h-12 text-primary" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2 text-foreground">Jsem připraven pomoci</h3>
                            <p className="text-sm text-muted-foreground max-w-sm">
                                Zeptej se mě na příklady z OSP, požádej o vysvětlení logických úloh nebo si nech zkontrolovat postup.
                            </p>
                        </div>
                    )}

                    {messages.map((msg, index) => (
                        <ChatMessageBubble
                            key={msg.id}
                            message={msg}
                            isLatest={index === messages.length - 1}
                            onActionClick={handleSendMessage}
                        />
                    ))}

                    {/* Limit Reached Message Inline - Only show when NOT loading */}
                    {limitReached && !isLoading && (
                        <div className="flex flex-col items-center justify-center p-6 my-4 bg-muted/50 rounded-xl border border-destructive/20 text-center animate-in fade-in zoom-in duration-300">
                            <Lock className="w-8 h-8 text-destructive mb-2" />
                            <h4 className="font-semibold text-foreground">Denní limit vyčerpán</h4>
                            <p className="text-sm text-muted-foreground mb-4">
                                {isPro ? "Dosáhli jste denního limitu 50 dotazů. Tutor si potřebuje odpočinout, vraťte se zítra!" : "Využili jste 5 testovacích dotazy zdarma."}
                            </p>
                            {!isPro && (
                                <button
                                    onClick={() => navigate('/upgrade')}
                                    className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
                                >
                                    <Zap size={16} />
                                    Odemknout neomezený přístup
                                </button>
                            )}
                        </div>
                    )}

                    {isLoading && streamBufferRef.current?.fullText === "" && (
                        <div className="flex justify-start mb-6 w-full animate-in fade-in duration-300 pl-4">
                            <div className="w-24 h-24 opacity-90">
                                <DotLottieReact
                                    src="/materials/AI Searching.lottie"
                                    loop
                                    autoplay
                                />
                            </div>
                        </div>
                    )}

                    {/* Starter Chips */}
                    {!messages.some(m => m.sender === Sender.USER) && !isLoading && (
                        <div className="flex flex-wrap gap-2 mb-4 px-2 justify-center">
                            {OSP_STARTER_CHIPS.map((chip) => (
                                <button
                                    key={chip.id}
                                    onClick={() => handleSendMessage(chip.prompt)}
                                    disabled={isLoading || limitReached}
                                    className="bg-gray-100 dark:bg-gray-200 hover:bg-white dark:hover:bg-white border border-gray-300 dark:border-gray-100 text-gray-900 dark:text-gray-900 px-4 py-2 rounded-full text-sm font-medium shadow-sm transition-all duration-200 active:bg-amber-400 active:text-black active:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-left max-w-full truncate backdrop-blur-md"
                                    aria-label={chip.label}
                                >
                                    {chip.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <div ref={messagesEndRef} className="h-4" />
                </div>
            </div>

            {/* Input Area */}
            <div className="px-4 py-2 md:px-6 md:py-4 bg-background/95 backdrop-blur-md border-t border-border">
                <div className="max-w-3xl mx-auto relative">
                    <div className={cn(
                        "relative flex items-end gap-2 bg-card border rounded-2xl overflow-hidden shadow-sm transition-all duration-200",
                        (isLoading || limitReached) ? "opacity-80" : "hover:border-primary/50 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20",
                        limitReached && "bg-muted cursor-not-allowed"
                    )}>
                        <textarea
                            ref={inputRef}
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={limitReached ? "Limit vyčerpán..." : "Na co se chceš zeptat?"}
                            className="w-full bg-transparent border-0 px-4 py-4 max-h-48 min-h-[56px] resize-none focus:ring-0 text-foreground placeholder-muted-foreground disabled:cursor-not-allowed"
                            rows={1}
                            disabled={isLoading || limitReached}
                        />
                        <button
                            onClick={() => handleSendMessage()}
                            disabled={!inputText.trim() || isLoading || limitReached}
                            className={cn(
                                "mb-2 mr-2 p-2 rounded-xl transition-all duration-200 flex-shrink-0",
                                !inputText.trim() || isLoading || limitReached
                                    ? "text-muted-foreground bg-muted cursor-not-allowed"
                                    : "bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 shadow-md"
                            )}
                        >
                            <Send size={18} />
                        </button>
                    </div>
                </div>
            </div>



            {/* BLOCKED ABUSE MODAL */}
            <AnimatePresence>
                {isBlocked && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 z-[60] bg-background/95 backdrop-blur-md flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="bg-card w-full max-w-md rounded-2xl border border-destructive/50 shadow-2xl shadow-destructive/20 overflow-hidden p-8 text-center"
                        >
                            <div className="w-20 h-20 bg-destructive/10 rounded-full mx-auto flex items-center justify-center mb-6">
                                <AlertCircle className="w-10 h-10 text-destructive" />
                            </div>
                            <h3 className="text-2xl font-bold mb-2 text-destructive">Přístup Omezen 🚫</h3>
                            <p className="text-muted-foreground mb-8">
                                Je nám líto, ale váš účet byl označen pro porušení podmínek (zneužití zkušebního období).
                                Váš přístup k OSP AI Pro byl zrušen.
                            </p>

                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={async () => {
                                        if (!currentUser) return;
                                        try {
                                            const token = await currentUser.getIdToken();
                                            const response = await fetch('/.netlify/functions/create-checkout-session', {
                                                method: 'POST',
                                                headers: {
                                                    'Content-Type': 'application/json',
                                                    'Authorization': `Bearer ${token}`
                                                },
                                                body: JSON.stringify({
                                                    priceId: 'price_1SkLqYCR1GWecDhITnZoJjNH',
                                                    productId: 'osp_ai_pro',
                                                    mode: 'subscription'
                                                })
                                            });
                                            const data = await response.json();
                                            if (data.url) window.location.href = data.url;
                                        } catch (e) {
                                            alert("Chyba při inicializaci platby.");
                                        }
                                    }}
                                    className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 rounded-xl font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                                >
                                    <Zap className="w-4 h-4" />
                                    Odblokovat a předplatit (444 Kč)
                                </button>

                                <button
                                    onClick={() => window.location.href = '/'}
                                    className="w-full bg-secondary text-secondary-foreground py-3 rounded-xl font-medium hover:opacity-90 transition-opacity"
                                >
                                    Zpět na přehled
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>


        </div>
    );
};

export default TutorModule;
